import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../utils/axios';
import toast from 'react-hot-toast';
import {
  ArrowPathIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  BuildingOfficeIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  TruckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

// ============================================
// Component: AgentReturnsPage
// Purpose: View and create returns for assigned companies
// ============================================

const STATUS_CONFIG = {
  requested: { label: 'בקשה חדשה', color: 'blue', icon: ClockIcon },
  pending_approval: { label: 'ממתין לאישור', color: 'yellow', icon: ClockIcon },
  approved: { label: 'אושר', color: 'green', icon: CheckCircleIcon },
  rejected: { label: 'נדחה', color: 'red', icon: XCircleIcon },
  label_sent: { label: 'תווית נשלחה', color: 'purple', icon: TruckIcon },
  in_transit: { label: 'בדרך', color: 'indigo', icon: TruckIcon },
  received: { label: 'התקבל', color: 'teal', icon: CheckCircleIcon },
  inspecting: { label: 'בבדיקה', color: 'orange', icon: MagnifyingGlassIcon },
  completed: { label: 'הושלם', color: 'green', icon: CheckCircleIcon },
  cancelled: { label: 'בוטל', color: 'gray', icon: XCircleIcon }
};

const AgentReturnsPage = () => {
  const navigate = useNavigate();
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchReturns();
    fetchStats();
  }, [statusFilter]);

  const fetchReturns = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter) params.status = statusFilter;
      
      const response = await axiosInstance.get('/api/field-agent/returns', { params });
      setReturns(response.data.returns || []);
    } catch (error) {
      console.error('Error fetching returns:', error);
      if (error.response?.status === 403 && error.response?.data?.feature_disabled) {
        toast.error('מערכת ההחזרות לא מופעלת');
      } else {
        toast.error('שגיאה בטעינת בקשות החזרה');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axiosInstance.get('/api/field-agent/returns/stats/summary');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(amount || 0);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('he-IL');
  };

  const filteredReturns = returns.filter(r => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      r.rma_number?.toLowerCase().includes(search) ||
      r.company?.name?.toLowerCase().includes(search) ||
      r.order?.order_number?.toLowerCase().includes(search)
    );
  });

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || { label: status, color: 'gray' };
    const colorClasses = {
      blue: 'bg-blue-100 text-blue-800',
      yellow: 'bg-yellow-100 text-yellow-800',
      green: 'bg-green-100 text-green-800',
      red: 'bg-red-100 text-red-800',
      purple: 'bg-purple-100 text-purple-800',
      indigo: 'bg-indigo-100 text-indigo-800',
      teal: 'bg-teal-100 text-teal-800',
      orange: 'bg-orange-100 text-orange-800',
      gray: 'bg-gray-100 text-gray-800'
    };
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${colorClasses[config.color]}`}>
        {config.icon && <config.icon className="h-3 w-3" />}
        {config.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4" dir="rtl">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">בקשות החזרה</h1>
          <p className="text-sm text-gray-500 mt-1">ניהול החזרות ללקוחות העסקיים שלך</p>
        </div>
        <button
          onClick={() => navigate('/agent/returns/new')}
          className="mt-4 md:mt-0 flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
        >
          <PlusIcon className="h-5 w-5" />
          בקשת החזרה חדשה
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">סה"כ בקשות</div>
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">ממתינות</div>
            <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">אושרו</div>
            <div className="text-2xl font-bold text-green-600">{stats.approved}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">שווי כולל</div>
            <div className="text-2xl font-bold text-primary-600">{formatCurrency(stats.totalValue)}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="חפש לפי מספר RMA, חברה או הזמנה..."
              className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          
          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">כל הסטטוסים</option>
            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
              <option key={key} value={key}>{config.label}</option>
            ))}
          </select>

          <button
            onClick={fetchReturns}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
          >
            <ArrowPathIcon className="h-5 w-5" />
            רענן
          </button>
        </div>
      </div>

      {/* Returns List */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      ) : filteredReturns.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <ArrowPathIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">אין בקשות החזרה</h3>
          <p className="text-gray-500 mb-4">לחץ על "בקשת החזרה חדשה" כדי ליצור בקשה ראשונה</p>
          <button
            onClick={() => navigate('/agent/returns/new')}
            className="inline-flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
          >
            <PlusIcon className="h-5 w-5" />
            בקשת החזרה חדשה
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">מספר RMA</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">חברה</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">הזמנה</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">פריטים</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">סכום</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">סטטוס</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">תאריך</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredReturns.map((returnItem) => (
                  <tr
                    key={returnItem.id}
                    onClick={() => navigate(`/agent/returns/${returnItem.id}`)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <span className="font-mono text-sm font-medium text-primary-600">
                        {returnItem.rma_number}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <BuildingOfficeIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">
                          {returnItem.company?.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600 font-mono">
                        {returnItem.order?.order_number}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">
                        {returnItem.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0} פריטים
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-medium text-gray-900">
                        {formatCurrency(
                          parseFloat(returnItem.store_credit_amount || 0) > 0 
                            ? returnItem.store_credit_amount 
                            : parseFloat(returnItem.total_refund_amount || 0) > 0
                              ? returnItem.total_refund_amount
                              : returnItem.items?.reduce((sum, item) => sum + parseFloat(item.refund_amount || 0), 0) || 0
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(returnItem.status)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-500">
                        {formatDate(returnItem.created_at)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentReturnsPage;


